import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, doc, increment } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Send, Search, MoreVertical, User as UserIcon, ArrowLeft, Loader2, Check, CheckCheck, MessageSquare, LogIn } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { resolveAvatar, avatarAt } from '../constants/sampleImages';
import { useNavigate } from 'react-router-dom';

const GUEST_UID = 'guest-preview';

const DEMO_CONVERSATIONS = [
  {
    id: 'demo-1',
    participants: [GUEST_UID, 'vendor-1'],
    participantNames: { 'vendor-1': 'Ceylon Royal Gems' },
    participantAvatars: { 'vendor-1': avatarAt(0) },
    lastMessage: 'Yes, this sapphire is GIA certified and unheated.',
    lastSenderId: 'vendor-1',
    lastUpdatedAt: { toDate: () => new Date(Date.now() - 45 * 60 * 1000) },
    unreadCount: { [GUEST_UID]: 1 },
  },
  {
    id: 'demo-2',
    participants: [GUEST_UID, 'vendor-2'],
    participantNames: { 'vendor-2': 'Ratnapura Gem House' },
    participantAvatars: { 'vendor-2': avatarAt(2) },
    lastMessage: 'Is the padparadscha still available?',
    lastSenderId: GUEST_UID,
    lastUpdatedAt: { toDate: () => new Date(Date.now() - 3 * 60 * 60 * 1000) },
    unreadCount: { [GUEST_UID]: 0 },
  },
  {
    id: 'demo-3',
    participants: [GUEST_UID, 'vendor-3'],
    participantNames: { 'vendor-3': 'Beruwala Star Traders' },
    participantAvatars: { 'vendor-3': avatarAt(4) },
    lastMessage: 'I can arrange a video call to show the stone under natural light.',
    lastSenderId: 'vendor-3',
    lastUpdatedAt: { toDate: () => new Date(Date.now() - 24 * 60 * 60 * 1000) },
    unreadCount: { [GUEST_UID]: 0 },
  },
];

const DEMO_MESSAGES = {
  'demo-1': [
    {
      id: 'm1',
      senderId: GUEST_UID,
      text: 'Hi, I\'m interested in the 3.2ct Ceylon blue sapphire. Is it still available?',
      createdAt: { toDate: () => new Date(Date.now() - 2 * 60 * 60 * 1000) },
      readBy: [GUEST_UID, 'vendor-1'],
    },
    {
      id: 'm2',
      senderId: 'vendor-1',
      text: 'Hello! Yes, it\'s still available. It\'s a stunning royal blue with excellent clarity.',
      createdAt: { toDate: () => new Date(Date.now() - 90 * 60 * 1000) },
      readBy: [GUEST_UID, 'vendor-1'],
    },
    {
      id: 'm3',
      senderId: GUEST_UID,
      text: 'Great. Does it come with certification?',
      createdAt: { toDate: () => new Date(Date.now() - 60 * 60 * 1000) },
      readBy: [GUEST_UID, 'vendor-1'],
    },
    {
      id: 'm4',
      senderId: 'vendor-1',
      text: 'Yes, this sapphire is GIA certified and unheated. I can share the report if you\'d like.',
      createdAt: { toDate: () => new Date(Date.now() - 45 * 60 * 1000) },
      readBy: ['vendor-1'],
    },
  ],
  'demo-2': [
    {
      id: 'm1',
      senderId: GUEST_UID,
      text: 'Is the padparadscha still available?',
      createdAt: { toDate: () => new Date(Date.now() - 3 * 60 * 60 * 1000) },
      readBy: [GUEST_UID],
    },
  ],
  'demo-3': [
    {
      id: 'm1',
      senderId: GUEST_UID,
      text: 'Could you tell me more about the star ruby listing?',
      createdAt: { toDate: () => new Date(Date.now() - 26 * 60 * 60 * 1000) },
      readBy: [GUEST_UID, 'vendor-3'],
    },
    {
      id: 'm2',
      senderId: 'vendor-3',
      text: 'Of course! It\'s a 5.1ct Sri Lankan star ruby with a sharp asterism.',
      createdAt: { toDate: () => new Date(Date.now() - 25 * 60 * 60 * 1000) },
      readBy: [GUEST_UID, 'vendor-3'],
    },
    {
      id: 'm3',
      senderId: 'vendor-3',
      text: 'I can arrange a video call to show the stone under natural light.',
      createdAt: { toDate: () => new Date(Date.now() - 24 * 60 * 60 * 1000) },
      readBy: [GUEST_UID, 'vendor-3'],
    },
  ],
};

export const ChatPage = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const isLoggedIn = !!userProfile;
  const currentUid = userProfile?.uid ?? GUEST_UID;
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(isLoggedIn);
  const [mobileView, setMobileView] = useState('list');
  const scrollRef = useRef(null);

  const displayConversations = useMemo(
    () => (isLoggedIn ? conversations : DEMO_CONVERSATIONS),
    [isLoggedIn, conversations]
  );

  useEffect(() => {
    setActiveConversationId(null);
    setMobileView('list');
    setNewMessage('');
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userProfile.uid),
      orderBy('lastUpdatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setConversations(convs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isLoggedIn, userProfile]);

  useEffect(() => {
    if (!activeConversationId) {
      setMessages([]);
      return;
    }

    if (!isLoggedIn) {
      setMessages(DEMO_MESSAGES[activeConversationId] || []);
      return;
    }

    const q = query(
      collection(db, 'conversations', activeConversationId, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);

      if (msgs.length > 0 && userProfile) {
        const lastMsg = msgs[msgs.length - 1];
        if (!lastMsg.readBy.includes(userProfile.uid)) {
          updateDoc(doc(db, 'conversations', activeConversationId), {
            [`unreadCount.${userProfile.uid}`]: 0
          });
        }
      }
    });

    return () => unsubscribe();
  }, [activeConversationId, isLoggedIn, userProfile]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    if (!newMessage.trim() || !activeConversationId || !userProfile) return;

    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      const activeConv = conversations.find(c => c.id === activeConversationId);
      if (!activeConv) return;

      const otherParticipantId = activeConv.participants.find(p => p !== userProfile.uid);

      await addDoc(collection(db, 'conversations', activeConversationId, 'messages'), {
        conversationId: activeConversationId,
        senderId: userProfile.uid,
        senderName: userProfile.displayName,
        text: messageText,
        createdAt: serverTimestamp(),
        readBy: [userProfile.uid]
      });

      const updateData = {
        lastMessage: messageText,
        lastSenderId: userProfile.uid,
        lastUpdatedAt: serverTimestamp(),
      };

      if (otherParticipantId) {
        updateData[`unreadCount.${otherParticipantId}`] = increment(1);
      }

      await updateDoc(doc(db, 'conversations', activeConversationId), updateData);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const selectConversation = (id) => {
    setActiveConversationId(id);
    setMobileView('chat');
  };

  const activeConversation = displayConversations.find(c => c.id === activeConversationId);
  const otherParticipantName = activeConversation
    ? Object.entries(activeConversation.participantNames).find(([uid]) => uid !== currentUid)?.[1] || 'Unknown User'
    : '';
  const otherParticipantAvatar = activeConversation
    ? Object.entries(activeConversation.participantAvatars).find(([uid]) => uid !== currentUid)?.[1]
    : undefined;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare size={24} className="text-primary" />
            Messages
          </h1>
          <p className="text-slate-500 text-sm">
            {isLoggedIn
              ? 'Chat with gemstone sellers and industry experts.'
              : 'Preview conversations with verified dealers. Log in to message sellers.'}
          </p>
        </div>
        {!isLoggedIn && (
          <button
            onClick={() => navigate('/login')}
            className="bg-primary text-white px-5 py-2.5 rounded-2xl font-bold shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2 text-sm whitespace-nowrap self-start md:self-auto"
          >
            <LogIn size={18} />
            Login to Chat
          </button>
        )}
      </div>

      {/* Chat Container */}
      <div className="h-[calc(100vh-14rem)] flex bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Sidebar - Conversation List */}
        <div className={cn(
          "w-full md:w-80 border-r border-slate-200 flex flex-col bg-slate-50/50",
          mobileView === 'chat' ? 'hidden md:flex' : 'flex'
        )}>
          <div className="p-4 border-b border-slate-200 bg-white">
            <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Conversations</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
              </div>
            ) : displayConversations.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-14 h-14 bg-primary/5 rounded-full flex items-center justify-center text-primary mx-auto mb-4">
                  <MessageSquare className="w-7 h-7" />
                </div>
                <p className="text-slate-500 text-sm">No conversations yet.</p>
                <p className="text-slate-400 text-xs mt-1">Inquire about a gem to start chatting.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {displayConversations.map((conv) => {
                  const otherUid = conv.participants.find(p => p !== currentUid);
                  const name = conv.participantNames[otherUid] || 'User';
                  const avatar = conv.participantAvatars[otherUid];
                  const isActive = activeConversationId === conv.id;
                  const unread = conv.unreadCount?.[currentUid] || 0;

                  return (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv.id)}
                      className={cn(
                        "w-full p-4 flex items-start gap-3 transition-colors text-left group",
                        isActive ? "bg-primary/5 border-l-2 border-primary" : "hover:bg-white border-l-2 border-transparent"
                      )}
                    >
                      <div className="relative flex-shrink-0">
                        {avatar ? (
                          <img
                            src={resolveAvatar(avatar)}
                            alt={name}
                            className="w-12 h-12 rounded-full object-cover ring-2 ring-primary/10"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary ring-2 ring-primary/10">
                            <UserIcon className="w-6 h-6" />
                          </div>
                        )}
                        {unread > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                            {unread}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className={cn(
                            "font-bold truncate text-sm",
                            unread > 0 ? "text-slate-900" : "text-slate-700"
                          )}>{name}</h3>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap font-medium">
                            {conv.lastUpdatedAt ? format(conv.lastUpdatedAt.toDate(), 'HH:mm') : ''}
                          </span>
                        </div>
                        <p className={cn(
                          "text-xs truncate",
                          unread > 0 ? "text-slate-900 font-medium" : "text-slate-500"
                        )}>
                          {conv.lastSenderId === currentUid && 'You: '}{conv.lastMessage || 'Start a conversation'}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className={cn(
          "flex-1 flex flex-col bg-white min-w-0",
          mobileView === 'list' ? 'hidden md:flex' : 'flex'
        )}>
          {activeConversationId ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-white">
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    onClick={() => setMobileView('list')}
                    className="md:hidden p-2 -ml-2 text-slate-500 hover:bg-slate-50 rounded-xl transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="relative flex-shrink-0">
                    {otherParticipantAvatar ? (
                      <img
                        src={resolveAvatar(otherParticipantAvatar)}
                        alt={otherParticipantName}
                        className="w-10 h-10 rounded-full object-cover ring-2 ring-primary/10"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary ring-2 ring-primary/10">
                        <UserIcon className="w-5 h-5" />
                      </div>
                    )}
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-white rounded-full" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-bold text-slate-900 text-sm truncate">{otherParticipantName}</h2>
                    <p className="text-[10px] text-success font-black uppercase tracking-widest">Online</p>
                  </div>
                </div>
                <button className="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-xl transition-colors flex-shrink-0">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>

              {/* Messages Area */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 space-y-4 bg-background custom-scrollbar"
              >
                <AnimatePresence initial={false}>
                  {messages.map((msg, index) => {
                    const isMe = msg.senderId === currentUid;
                    const showDate = index === 0 ||
                      format(msg.createdAt?.toDate() || new Date(), 'yyyy-MM-dd') !==
                      format(messages[index - 1].createdAt?.toDate() || new Date(), 'yyyy-MM-dd');

                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <div className="flex justify-center my-6">
                            <span className="px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] text-slate-400 font-black shadow-sm uppercase tracking-widest">
                              {format(msg.createdAt?.toDate() || new Date(), 'MMMM d, yyyy')}
                            </span>
                          </div>
                        )}
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          className={cn(
                            "flex group",
                            isMe ? "justify-end" : "justify-start"
                          )}
                        >
                          <div className={cn(
                            "max-w-[75%] rounded-2xl p-3 shadow-sm relative",
                            isMe
                              ? "bg-primary text-white rounded-tr-sm"
                              : "bg-white text-slate-800 border border-slate-200 rounded-tl-sm"
                          )}>
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                            <div className={cn(
                              "flex items-center gap-1 mt-1 justify-end",
                              isMe ? "text-white/70" : "text-slate-400"
                            )}>
                              <span className="text-[10px] font-medium">
                                {msg.createdAt ? format(msg.createdAt.toDate(), 'HH:mm') : '...'}
                              </span>
                              {isMe && (
                                msg.readBy.length > 1 ? (
                                  <CheckCheck className="w-3 h-3 text-white" />
                                ) : (
                                  <Check className="w-3 h-3" />
                                )
                              )}
                            </div>
                          </div>
                        </motion.div>
                      </React.Fragment>
                    );
                  })}
                </AnimatePresence>
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-slate-200 bg-white">
                {isLoggedIn ? (
                  <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 bg-white border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all shadow-sm"
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="p-3 bg-primary text-white rounded-2xl hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100 transition-all shadow-lg shadow-primary/20"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </form>
                ) : (
                  <div className="bg-primary/5 border border-primary/10 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="text-sm text-slate-600 text-center sm:text-left">
                      Log in to send messages and start real conversations with sellers.
                    </p>
                    <button
                      onClick={() => navigate('/login')}
                      className="bg-primary text-white px-5 py-2.5 rounded-2xl font-bold shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-2 text-sm whitespace-nowrap"
                    >
                      <LogIn size={16} />
                      Login / Register
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-background">
              <div className="w-20 h-20 bg-primary/5 rounded-full flex items-center justify-center text-primary mb-6 ring-4 ring-primary/10">
                <MessageSquare className="w-10 h-10" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Your Messages</h2>
              <p className="text-slate-500 max-w-sm text-sm">
                {isLoggedIn
                  ? 'Select a conversation to start chatting with gemstone sellers and industry experts.'
                  : 'Select a preview conversation on the left, or log in to message sellers directly.'}
              </p>
              {!isLoggedIn && (
                <button
                  onClick={() => navigate('/login')}
                  className="mt-6 bg-primary text-white px-6 py-2.5 rounded-2xl font-bold shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all text-sm"
                >
                  Login to Chat
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
